/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { CertsApiService, FallbackPagesApiService } from '../../../../shared/http';
import { Component, HostListener, ViewChild } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LbErrorService } from '../../../../shared/services/components';
import {
    LanguageService,
    LogService,
    NotifyService,
    RbacService,
    UtilsService,
    ValidationService
} from '../../../../../../core/services';
import { forkJoin, Observable } from 'rxjs';
import { nameValidation, portRangeValidation } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-edit-fallback',
    templateUrl: './addEditFallbackPage.component.html',
    styleUrls: ['./addEditFallbackPage.component.scss']
})
export class AddEditFallbackPageComponent extends BaseComponent {
    /**
     * Reference to the text area.
     */
    @ViewChild('htmlTextAreaElement') public textarea: any;

    /**
     * Reference to the line numbers.
     */
    @ViewChild('lines') public lines: any;

    /**
     * Title for the page.
     */
    public pageTitle: string;

    /**
     * Fallback page form.
     */
    public pageForm: FormGroup;

    /**
     * Store the id of the fallback page to edit.
     */
    public fallbackId: string;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        certificates: [],
        types: [],
    };

    /**
     * Number of lines for text area on load.
     */
    public numberOfLines: number = 10;

    /**
     * Text area line numbers.
     */
    public lineNumbers: number[] = [...Array(this.numberOfLines + 1).keys()].slice(1);

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Embedded in wizard flag.
     */
    public embedded: boolean = false;

    /**
     * Store callback function.
     */
    public callBack: (...args: any[]) => void;

    /**
     * Store the initial data for the form.
     * @private
     */
    private initialFormData: any = {};

    /**
     * Store the certificates.
     * @private
     */
    private certs: any[] = [];

    /**
     * Store the fallback pages.
     * @private
     */
    private fallbackPages: any[] = [];

    constructor(
        private certsApiService: CertsApiService,
        private fallbackApiService: FallbackPagesApiService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private lbErrorService: LbErrorService,
        private logService: LogService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();
        this.fallbackId = this.route.snapshot.params['id'];
        this.setupForm();
        this.setupFormSubscribers();
        this.setPageTitle();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Add a host listener to capture the paste event.
     * @param event
     */
    @HostListener('paste', ['$event'])
    public onPasteHandler(event: ClipboardEvent): void {
        const pastedData: string = event.clipboardData.getData('Text');
        const dataLines: string[] = pastedData.split('\n');
        if (dataLines.length > 10) {
            this.updateLineNumbers();
        }
    }

    /**
     * Add a host listener for checking the enter key.
     * @param event
     */
    @HostListener('keydown', ['$event'])
    public onKeydownHandler(event: KeyboardEvent): void {
        const value: string = window.getSelection().toString();
        const lineNums: string[] = value.split('\n');

        if (event.key === 'Tab' && event.shiftKey) {
            event.preventDefault();
            if (lineNums.length > 1) {
                this.handleBlockIndentation('back');
            }
        }

        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            const { selectionStart } = this.textarea.nativeElement;
            if (lineNums.length > 1) {
                this.handleBlockIndentation('forward');
            } else {
                this.textarea.nativeElement.setRangeText('\t', selectionStart, selectionStart, 'end');
            }
        }
    }

    /**
     * Add a host listener to capture the keyup event.
     * @param event
     */
    @HostListener('keyup', ['$event'])
    public onKeyUpListener(event: KeyboardEvent): void {
        if (event.key === 'Backspace' && this.lineNumbers.length > 10) {
            this.updateLineNumbers();
        }
    }

    /**
     * Adds and removes rows from the text area
     * @param event
     */
    public changeRowLength(event: any): void {
        const rows = this.textarea.nativeElement.rows;
        const activeRow = event.target.value?.substr(0, event.target.selectionStart).split('\n').length;

        // Add row
        if (event.key === 'Enter' && (this.lineNumbers.length <= activeRow)) {
            this.textarea.nativeElement.rows = rows + 1;
            this.lineNumbers.push(this.lineNumbers.length + 1);
        }

        // Remove row
        if (event.key === 'Backspace') {
            const value = this.pageForm.get('pageContent').value;
            const lineValues = value.split('\n');

            if (activeRow && lineValues[activeRow - 1].length < 1 && event.target.selectionStart > 0) {
                this.textarea.nativeElement.rows = rows - 1;
                this.lineNumbers.pop();
            }
        }
    }

    /**
     * Make the api call with the action and id.
     * @private
     */
    public submit(): Observable<any> {
        return new Observable((observer) => {
            const data = this.pageForm.value;
            if (data.certId === 'none') {
                data.certId = '';
            }
            // If the current ID is an empty string then add the page else edit the page.
            const action = data.id === '' ? 'add' : 'edit';
            this.fallbackApiService[action](data)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.cancel();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.notifyService.removeNotificationById('fallback');
                        this.lbErrorService.pageWithErrors(this.pageForm, error.error.errors);
                        this.logService.error('FallBackPagesComponent: fallBackApiCall',
                            `Unable to ${{ action }} the fallback page.`, error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Navigate away from component view.
     */
    public cancel(): void {
        if (this.embedded) {
            this.callBack();
        } else {
            this.navigateToParent();
        }
    }

    /**
     * Set up the data for the page.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            certs: this.certsApiService.getCerts(),
            pages: this.fallbackApiService.getAll()
        };

        if (this.fallbackId) {
            apiCalls['page'] = this.fallbackApiService.get(this.fallbackId);
        }

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    this.certs = dataGroup['certs'];
                    this.fallbackPages = dataGroup['pages'];
                    this.setUpDropdowns();
                    this.setInputsFromType('internal');

                    if (dataGroup['page']) {
                        const pageDetails: any = dataGroup['page'];
                        this.updateLineNumbers();
                        pageDetails.certId = pageDetails.certId === null ? 'none' : pageDetails.certId;
                        this.pageForm.patchValue(pageDetails);
                        this.initialFormData = this.pageForm.value;
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AddEditFallbackPageComponent: setUpPageData', 'failed to get data.', error);
                }
            });
    }

    /**
     * Update page content line numbers.
     * @private
     */
    private updateLineNumbers() {
        const updateTimeout = setTimeout(() => {
            const content = this.textarea.nativeElement.value;
            const contentLines = content.split('\n');
            this.setLineNumbers(contentLines.length);
            clearTimeout(updateTimeout);
        }, 0);
    }

    /**
     * Handle multi-line indentation.
     * @param direction
     * @private
     */
    private handleBlockIndentation(direction: string): void {
        const { value, selectionStart, selectionEnd } = this.textarea.nativeElement;
        const linesBeforeCaretStart = value.substring(0, selectionStart).split('\n');
        const linesBeforeCaretEnd = value.substring(0, selectionEnd).split('\n');
        const startLine: number = direction === 'forward' ? linesBeforeCaretStart.length : linesBeforeCaretStart.length - 1;
        const endLine: number = direction === 'forward' ? linesBeforeCaretEnd.length : linesBeforeCaretEnd.length - 1;

        // Created function for readability.
        this.updateTextPosition({ direction, startLine, endLine, value });
        const startLineText = linesBeforeCaretStart[startLine];
        const positionObj = { direction, startLineText, startLine, endLine, selectionStart, selectionEnd, value };
        this.updateCursorPositions(positionObj);
    }

    /**
     * Update position of text after tabbing.
     * @param direction
     * @param value
     * @param startLine
     * @param endLine
     * @private
     */
    private updateTextPosition({ direction, value, startLine, endLine }): void {
        if (direction === 'forward') {
            this.textarea.nativeElement.value = value
                .split('\n')
                .map((line: any, i: number) => (i >= startLine - 1 && i <= endLine - 1) ? `\t${line}` : line)
                .join('\n');
        } else {
            this.textarea.nativeElement.value = value
                .split('\n')
                .map((line: string, i: number) => i >= startLine && i <= endLine && line.startsWith('\t') ? line.substring(1) : line)
                .join('\n');
        }
    }

    /**
     * Update cursor position.
     * @param positionObj
     * @private
     */
    private updateCursorPositions(positionObj: any): void {
        const { direction, startLineText, selectionStart, selectionEnd, value, startLine, endLine } = positionObj;
        if (direction === 'forward') {
            this.textarea.nativeElement.selectionStart = startLineText && /\S/.test(startLineText)
                ? selectionStart + 1
                : selectionStart;
            this.textarea.nativeElement.selectionEnd = selectionEnd + (endLine - startLine + 1);
        } else {
            this.textarea.nativeElement.selectionStart = startLineText?.startsWith('\t')
                ? selectionStart - 1
                : selectionStart;
            this.textarea.nativeElement.selectionEnd = selectionEnd - (value.length - this.textarea.nativeElement.value.length);
        }
    }

    /**
     * Set the line numbers.
     * @param numbers
     * @private
     */
    private setLineNumbers(numbers: number): void {
        this.lineNumbers = [];
        for (let i = 1; i < numbers + 1; i++) {
            this.lineNumbers.push(i);
        }
    }

    /**
     * Setup drop down content.
     * @private
     */
    private setUpDropdowns(): void {
        // Set up object array for certificates to be stored in.
        const dropDownCerts = [{ key: 'none', value: 'None' }];
        // If certificates are available, loop through them and push to object array above.
        if (this.certs) {
            for (const cert of this.certs) {
                if (cert.id && cert.name) {
                    dropDownCerts.push({
                        key: cert.id,
                        value: cert.name
                    });
                }
            }
        }
        this.dropdown.certificates = dropDownCerts;

        this.dropdown.types = [
            { key: 'internal', value: 'Internal' },
            { key: 'external', value: 'External' }
        ];
    }

    /**
     * Set the page title depending on if there is a fallback page id.
     * @private
     */
    private setPageTitle(): void {
        this.pageTitle = this.fallbackId ? 'fallback_page_edit' : 'fallback_pages_add_page';
    }

    /**
     * Return to parent page.
     * @private
     */
    private navigateToParent(): void {
        this.router.navigate(
            [this.fallbackId ? '../..' : '..'],
            { relativeTo: this.route }).then();
    }

    /**
     * Setup page form.
     * @private
     */
    private setupForm(): void {
        const startValue = Array(this.numberOfLines).fill('\n').join('');
        this.pageForm = this.formBuilder.group({
            id: [''],
            name: [null, [Validators.required, nameValidation]],
            certId: ['none'],
            type: ['internal'],
            ip: [null],
            port: [null],
            pageContent: startValue  // This creates a string that matches the length of the textarea.
        });
    }

    /**
     * Subscribe To Form Changes
     * @private
     */
    private setupFormSubscribers(): void {
        this.pageForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('form', () => {
                        if (this.fallbackId) {
                            if (this.utilsService.hasFormDataChanged(this.initialFormData, form)) {
                                this.pageForm.markAsDirty();
                            } else {
                                this.pageForm.markAsPristine();
                            }
                        }
                    }, 250);
                }
            });

        this.pageForm.get('name').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: value => {
                    this.utilsService.debounce('labelValue', () => {
                        this.validateLabel(value);
                    }, 500);
                }
            });

        this.pageForm.get('type').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: type => {
                    this.setInputsFromType(type);
                }
            });
    }

    /**
     * Validate fallback page name.
     * @param name
     * @private
     */
    private validateLabel(name: string): void {
        const fallbackLabels = this.fallbackPages.map(row => {
            if (row.id !== this.pageForm.get('id').value) {
                return row.name;
            }
        });

        if (fallbackLabels.includes(name)) {
            this.pageForm.get('name').setErrors({ 'duplicateLabel': `${this.languageService.translate('duplicate_name')}` });
        }
    }

    /**
     * Sets up the enabled and disabled inputs based on the type selected.
     * @param type
     * @private
     */
    private setInputsFromType(type: string): void {
        if (type === 'internal') {
            this.pageForm.get('ip').disable();
            this.pageForm.get('port').disable();
            this.pageForm.get('pageContent').enable();
            this.pageForm.controls['pageContent'].setValidators([Validators.required]);
        } else {
            this.pageForm.get('ip').enable();
            this.pageForm.get('port').enable();
            this.pageForm.get('pageContent').disable();
            this.pageForm.controls['ip'].setValidators([Validators.required, ValidationService.ipDomainValidator]);
            this.pageForm.controls['port'].setValidators([Validators.required, portRangeValidation]);
        }
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = this.fallbackId
            ? { 'fallback-submit': [{ path: '/fallback/:id', methods: ['PUT'] }] }
            : { 'fallback-submit': [{ path: '/fallback', methods: ['POST'] }] };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup['fallback-submit']) {
            this.pageForm.disable();
        }
    }
}
